import { Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { Validators } from '@angular/forms'
import { FieldTree, form, hidden, PathKind, SchemaPath, SchemaPathRules } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'

import { BehaviorSubject, Observable, Subject } from 'rxjs'

import { DateService } from '@lemon/core/service/date.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { validoiReaktiivisesti } from '../../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { TimestampService } from '../../_jaettu-angular/service/timestamp-service'

import { Yritysmuoto } from '@lemon/core-firebase/model/kayttaja'
import { TuettuKieli } from '@lemon/core/model/common'
import { EmailTemplatesUriService } from 'app/_jaettu-lemonator/service/email-templates-uri.service'
import { EmailTemplateDraft, EmailTemplateVariable, TemplateAlvType, TemplateCategory } from '../../_jaettu-lemonator/model/email-templates'

interface SahkopostipohjaLomake {
  kieli: TuettuKieli
  textStart: string
  textMid: string
  textEnd: string
  regularOrZen: 'reg' | 'zen'
  yritysmuoto: Yritysmuoto
  hasAttachments: boolean
  hasSelvitettavia: boolean
  alvType: TemplateAlvType
}

type SahkopostipohjanKentta = keyof SahkopostipohjaLomake

const KIRJANPITOPOHJAN_KENTAT: SahkopostipohjanKentta[] = ['kieli', 'textStart', 'textMid', 'textEnd', 'regularOrZen', 'yritysmuoto', 'hasAttachments', 'hasSelvitettavia', 'alvType']

@Component({
  templateUrl: './email-templates.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EmailTemplatesComponent implements OnInit, OnDestroy {
  private _ngUnsubsribe: Subject<void> = new Subject<void>()

  sopimustenVersiotObservable: Observable<{ aika: string; tekstifi: string; tekstien: string; kirjanpitaja: string }[]>
  usableVariablesSuomiObservable: Observable<string[]>
  usableVariablesEnglishObservable: Observable<string[]>

  savedDataSubject: Subject<EmailTemplateDraft> = new Subject()

  templateCategories: { avain: TemplateCategory; nimi: string }[] = [
    { avain: TemplateCategory.KIRJANPITO, nimi: 'Kirjanpito' },
    { avain: TemplateCategory.VEROILMOITUS, nimi: 'Veroilmoitus' },
    { avain: TemplateCategory.TILINPAATOS, nimi: 'Tilinpäätös' },
    { avain: TemplateCategory.SOPIMUS_PAATTYY, nimi: 'Sopimus päättyy' },
    { avain: TemplateCategory.TAIKALINKKI, nimi: 'Taikalinkki' },
  ]

  alvTypes: { avain: TemplateAlvType; nimi: string }[] = [
    { avain: TemplateAlvType.ALV_1KK, nimi: 'Alvillinen 1kk' },
    { avain: TemplateAlvType.ALV_3KK12KK_EI_MAKSUKUUKAUSI, nimi: 'Alvillinen 3kk/12kk ei maksukuukausi' },
    { avain: TemplateAlvType.ALV_3KK12KK_MAKSUKUUKAUSI, nimi: 'Alvillinen 3kk/12kk maksukuukausi' },
    { avain: TemplateAlvType.ALVITON, nimi: 'Alviton' },
  ]

  yritysmuodot: { avain: Yritysmuoto; nimi: string }[] = [
    { avain: Yritysmuoto.TOIMINIMI, nimi: 'Toiminimi' },
    { avain: Yritysmuoto.OSAKEYHTIO, nimi: 'Osakeyhtiö' },
    { avain: Yritysmuoto.AVOINYHTIO, nimi: 'Avoin yhtiö' },
    { avain: Yritysmuoto.KOMMANDIITTIYHTIO, nimi: 'Kommandiittiyhtiö' },
    { avain: Yritysmuoto.OSUUSKUNTA, nimi: 'Osuuskunta' },
    { avain: Yritysmuoto.YHDISTYS, nimi: 'Yhdistys' },
  ]

  // selectedCategoryFormControl: FormControl<TemplateCategory> = new FormControl(TemplateCategory.KIRJANPITO, Validators.required)

  private readonly _lomakkeenKentat = signal<ReadonlySet<SahkopostipohjanKentta>>(new Set(['kieli']))

  lomake = form(
    signal<SahkopostipohjaLomake>({
      kieli: 'fi',
      textStart: '',
      textMid: '',
      textEnd: '',
      regularOrZen: 'reg',
      yritysmuoto: Yritysmuoto.TOIMINIMI,
      hasAttachments: false,
      hasSelvitettavia: false,
      alvType: TemplateAlvType.ALV_1KK,
    }),
    polku => {
      for (const kentta of KIRJANPITOPOHJAN_KENTAT) {
        const kenttaPolku = polku[kentta] as SchemaPath<unknown, SchemaPathRules.Supported, PathKind.Child>
        hidden(kenttaPolku, () => !this._lomakkeenKentat().has(kentta))
        validoiReaktiivisesti(kenttaPolku, Validators.required)
      }
    },
  )

  usableVariablesSubject: BehaviorSubject<EmailTemplateVariable[]> = new BehaviorSubject([])

  constructor(
    private _formValidationService: FormValidationService,
    private _firebase: FirebaseLemonator,
    private _ladataanService: LadataanService,
    private _timestampService: TimestampService,
    private _dateService: DateService,
    private _snackbar: MatSnackBar,
    private _error: ErrorHandler,
    private _dialog: MatDialog,
    private _emailTemplatesUriService: EmailTemplatesUriService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
  ) {}

  ngOnInit() {
    // const draftObservable = combineLatest([kieliObservable, yritysmuotoObservable, tavallinenTaiZenObservable, liitteetObservable, alvObservable, selvitettaviaObservable])
    //   .switchMap(([kieli, yritysmuoto, tavallinenTaiZen, liitteet, alv, selvitettavia]))
    // const valitunPohjanTekstitObservable: Observable<EmailTemplate> = combineLatest([
    //   this.yritysmuotoFormControl.valueChanges,
    //   this.pohjaTyyppiFormControl.valueChanges
    // ]).pipe(
    //   switchMap(([yritysmuoto, tyyppi]) => {
    //     if (!yritysmuoto && !tyyppi) {
    //       return observableOf(null)
    //     }
    //     const uri = this._sahkpostipohjatUriService.getSahkopostipohjaUri(yritysmuoto, tyyppi)
    //     return this._firebase.firestoreDoc<EmailTemplate>(uri).listen()
    //   })
    // )
    // valitunPohjanTekstitObservable.pipe(
    //   takeUntil(this._ngUnsubsribe)
    // ).subscribe(tekstit => {
    //   if (tekstit?.textInDiffLangs) {
    //     this.textFi.setValue(tekstit.textInDiffLangs.fi)
    //     this.textFi.setValue(tekstit.textInDiffLangs.en)
    //   }
    //   if (tekstit.supportedVariables) {
    //     return this.usableVariablesSubject.next(tekstit.supportedVariables)
    //   }
    // })
    // this.usableVariablesSuomiObservable = combineLatest([this.textFi.valueChanges, this.usableVariablesSubject]).pipe(
    //   takeUntil(this._ngUnsubsribe),
    //   map(([text, usableVariables]) => {
    //     if (!text) {
    //       return usableVariables
    //     }
    //     return usableVariables.filter(variable => !text.includes(variable))
    //   })
    // )
    // this.usableVariablesEnglishObservable = combineLatest([this.textEn.valueChanges, this.usableVariablesSubject]).pipe(
    //   takeUntil(this._ngUnsubsribe),
    //   map(([text, usableVariables]) => {
    //     if (!text) {
    //       return usableVariables
    //     }
    //     return usableVariables.filter(variable => !text.includes(variable))
    //   })
    // )
  }

  async save() {
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this._ladataanService.aloitaLataaminen()

    //   const kirjanpitajanTiedot = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable)

    //   const tyyppi = this.pohjaTyyppiFormControl.value
    //   const yritysmuoto = this.yritysmuotoFormControl.value
    //   const docData: EmailTemplate = {
    //     updatedAt: this._timestampService.now(),
    //     updatedBy: kirjanpitajanTiedot.uid,
    //     textInDiffLangs: {
    //       'fi': this.textFi.value,
    //       'en': this.textFi.value
    //     },
    //     tyyppi: tyyppi,
    //     yritysmuoto: yritysmuoto,
    //     supportedVariables: this.usableVariablesSubject.value
    //   }
    //   await this._firebase.firestoreSetData(this._sahkpostipohjatUriService.getSahkopostipohjaUri(yritysmuoto, tyyppi), docData)

    //   this._ladataanService.lopetaLataaminen()
    //   this._snackbar.open('Tallentaminen onnistui', 'OK', { duration: 5000 })
  }

  ngOnDestroy() {
    this._ngUnsubsribe.next()
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  private async _startKirjanpitoTemplate() {
    this._lomakkeenKentat.set(new Set(KIRJANPITOPOHJAN_KENTAT))

    const templateAvain = this._emailTemplatesUriService.calcTemplateAvain(
      TemplateCategory.KIRJANPITO,
      this.lomake.kieli().value(),
      this.lomake.regularOrZen().value(),
      this.lomake.alvType().value(),
      this.lomake.hasAttachments().value(),
      this.lomake.hasSelvitettavia().value(),
    )
    const data: EmailTemplateDraft = await this._firebase.firestoreDoc<EmailTemplateDraft>(this._emailTemplatesUriService.getEmailTemplateDraftUri(templateAvain)).get()

    this.savedDataSubject.next(data)
  }

  async changeSelectedCategory(category: TemplateCategory) {
    if (category === TemplateCategory.KIRJANPITO) {
      await this._startKirjanpitoTemplate()
    } else {
      this._lomakkeenKentat.set(new Set())
      console.error('Unknown template category!')
      // throw new Error('Unknown template category!')
    }
  }
}
